import authApi from './authApi';

export type NotificationType = 'appointment' | 'estimate' | 'work_order' | 'payment' | 'quote_request';

export interface Notification {
  id: number;
  user_id: number;
  notification_type: NotificationType;
  entity_type: string;
  entity_id: number;
  title: string;
  message: string;
  action_url: string | null;
  is_read: boolean;
  read_at: string | null;
  metadata: string | null;
  created_at: string;
}

export interface NotificationFilters {
  notification_type?: NotificationType;
  is_read?: boolean;
  entity_type?: string;
}

export interface NotificationCounts {
  appointment: number;
  estimate: number;
  work_order: number;
  payment: number;
  quote_request: number;
  total: number;
}

export interface NotificationsResponse {
  notifications: Notification[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

/**
 * Get notifications for the current user with filters and pagination
 */
export async function getNotifications(
  filters?: NotificationFilters,
  page: number = 1,
  limit: number = 20
): Promise<NotificationsResponse> {
  const response = await authApi.get<NotificationsResponse>('/admin/notifications', {
    params: {
      ...filters,
      page,
      limit,
    },
  });
  return response.data;
}

/**
 * Get unread notification counts by type
 */
export async function getUnreadCounts(): Promise<NotificationCounts> {
  const response = await authApi.get<NotificationCounts>('/admin/notifications/unread-counts');
  return response.data;
}

/**
 * Get recent notifications (last N)
 */
export async function getRecentNotifications(limit: number = 10): Promise<Notification[]> {
  const response = await authApi.get<Notification[]>('/admin/notifications/recent', {
    params: { limit },
  });
  return response.data;
}

/**
 * Mark a notification as read
 */
export async function markAsRead(notificationId: number): Promise<void> {
  await authApi.put(`/admin/notifications/${notificationId}/read`);
}

/**
 * Mark all notifications as read (optionally filtered by type)
 */
export async function markAllAsRead(notificationType?: NotificationType): Promise<{ message: string; count: number }> {
  const response = await authApi.put<{ message: string; count: number }>(
    '/admin/notifications/mark-all-read',
    {},
    {
      params: notificationType ? { notification_type: notificationType } : {},
    }
  );
  return response.data;
}

/**
 * Delete a notification
 */
export async function deleteNotification(notificationId: number): Promise<void> {
  await authApi.delete(`/admin/notifications/${notificationId}`);
}
